import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import ProjectCard from '@/components/ProjectCard';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { isDark } = useTheme();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [stats, setStats] = useState({ total_members: 50000, active_projects: 0, domains_explored: 10 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);
const getStatusColor = (status) => {
  switch (status) {
    case "Building":
      return "bg-pink-500";
    case "Testing":
      return "bg-blue-500";
    case "Ideation":
      return "bg-yellow-500";
    case "Live":
      return "bg-green-500";
    default:
      return "bg-gray-500";
  }
};

  const fetchData = async () => {
    try {
      const [projectsRes, statsRes] = await Promise.all([
        axios.get(`${API}/projects/featured`),
        axios.get(`${API}/stats`)
      ]);
      setFeaturedProjects(projectsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      number: 1,
      title: 'Ideation',
      subtitle: 'Spark the Idea',
      description: 'Pick a problem or submit your own. Define what you want to solve and why it matters.',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-500'
    },
    {
      number: 2,
      title: 'Validate',
      subtitle: 'Research & Insights',
      description: 'Understand your users and market before building. Back your idea with real research.',
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-500'
    },
    {
      number: 3,
      title: 'Build',
      subtitle: 'Bring Ideas to Life',
      description: 'Collaborate across tech, design, and business to turn your idea into a prototype or MVP.',
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-500'
    },
    {
      number: 4,
      title: 'Test',
      subtitle: 'With Real Users',
      description: 'Share your project with our 50K+ community, collect feedback, and improve it.',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500'
    },
    {
      number: 5,
      title: 'Launch',
      subtitle: 'Share It with the World',
      description: 'Once ready, launch your project publicly! Get featured across our social pages and community.',
      color: 'from-red-500 to-pink-600',
      bgColor: 'bg-red-500'
    }
  ];

  const scrollToHowItWorks = (e) => {
    e.preventDefault();
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="pt-20" data-testid="homepage">
      {/* Hero Section - Split Screen Layout */}
      <section className={`relative overflow-hidden transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e] text-white' 
          : 'bg-gradient-to-br from-white via-purple-50 to-pink-50 text-gray-900'
      }`} data-testid="hero-section">
        {/* Animated Background Effects */}
        <div className="absolute inset-0 overflow-hidden opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-center min-h-[600px] py-12 lg:py-20">
            {/* Centered Content */}
            <motion.div 
              className="space-y-6 lg:space-y-8 text-center max-w-4xl"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Badge */}
              <motion.div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
                  isDark 
                    ? 'bg-purple-600/20 border-purple-400/30 text-purple-300' 
                    : 'bg-purple-100 border-purple-300 text-purple-700'
                }`}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></span>
                Real World Product Challenge
              </motion.div>

              {/* Headline with Gradient Text */}
              <motion.h1 
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight"
                data-testid="hero-headline"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <span className="block mb-2 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                  Build Real Things.
                </span>
                <span className="block bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent animate-gradient">
                  Not Just Portfolios.
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                className={`text-base sm:text-lg lg:text-xl leading-relaxed max-w-3xl mx-auto ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}
                data-testid="hero-subheadline"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
              Join the Real World Product Challenge — where students, builders, and dreamers turn real problems into working projects.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/explore"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg shadow-purple-500/50 hover:shadow-xl"
                data-testid="hero-cta-explore"
              >
                Explore Projects
              </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/submit-idea"
                    className={`inline-flex items-center gap-2 bg-transparent border-2 px-8 py-3 rounded-full font-semibold transition shadow-lg ${
                      isDark 
                        ? 'border-white/80 text-white hover:bg-white/10' 
                        : 'border-purple-600 text-purple-600 hover:bg-purple-50'
                    }`}
                data-testid="hero-cta-submit"
              >
                Submit an Idea
              </Link>
                </motion.div>
              </motion.div>

              {/* Stats */}
              <motion.div
                className="flex flex-wrap justify-center gap-6 sm:gap-8 pt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
              >
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>50K+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Community Members</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>20+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active Projects</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>10+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Domains</div>
            </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Gradient Border */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500"></div>
      </section>

      {/* What We Do Section */}
      <section className={`py-20 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#1a0b2e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`} data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={`inline-block px-6 py-2 rounded-full text-sm font-semibold ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' 
                : 'bg-purple-100 text-purple-700 border border-purple-300'
            }`}>
              Our Mission
            </span>
          </motion.div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className={`text-4xl sm:text-5xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                What we do?
              </h2>
              
              <p className={`text-lg leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Prodizzy is a space for students and young professionals who want to move beyond just internships and theoretical work. Every week, we pick a real-world problem — something meaningful — and build a real solution together.
              </p>

              <p className={`text-lg leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We believe the best way to learn is by building. Join a community where your ideas transform into launched products, and your skills grow with every project.
              </p>

              <motion.div
                className="pt-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={scrollToHowItWorks}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg"
            data-testid="see-how-it-works-link"
          >
                  See How It Works
                  <ArrowRight size={20} />
                </button>
              </motion.div>
            </motion.div>

            {/* Right: Image/Illustration Placeholder */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className={`rounded-3xl p-8 ${
                isDark 
                  ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/20' 
                  : 'bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200'
              }`}>
                {/* Card Grid for "Real Problems, Real Solutions, Real Teams, Real Impact" */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { 
                      icon: (
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      ), 
                      title: 'Real Problems', 
                      desc: 'Tackle meaningful challenges that matter',
                      color: 'text-purple-500'
                    },
                    { 
                      icon: (
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      ), 
                      title: 'Real Solutions', 
                      desc: 'Build working products, not just concepts',
                      color: 'text-blue-500'
                    },
                    { 
                      icon: (
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      ), 
                      title: 'Real Teams', 
                      desc: 'Collaborate with passionate builders',
                      color: 'text-pink-500'
                    },
                    { 
                      icon: (
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      ), 
                      title: 'Real Impact', 
                      desc: 'Launch products that people use',
                      color: 'text-green-500'
                    }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      className={`p-6 rounded-2xl ${
                        isDark 
                          ? 'bg-[#1a0b2e] border border-purple-500/20' 
                          : 'bg-white border border-purple-200'
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      whileHover={{ y: -5, scale: 1.05 }}
                    >
                      <div className={`mb-3 ${item.color}`}>{item.icon}</div>
                      <h3 className={`font-bold text-lg mb-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {item.desc}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className={`py-20 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#1a0b2e] via-[#0f0520] to-[#1a0b2e]' 
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
      }`} data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={`inline-block px-6 py-2 rounded-full text-sm font-semibold ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' 
                : 'bg-purple-100 text-purple-700 border border-purple-300'
            }`}>
              The Process
            </span>
          </motion.div>

          {/* Title */}
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              How It Works
            </h2>
            <p className={`text-lg ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              From idea to impact — we guide you every step of the way.
            </p>
          </motion.div>

          {/* Steps - Horizontal Flow with Connected Design */}
          <div className="relative mt-16">
            {/* Connection Line - Desktop Only */}
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 opacity-20" 
                 style={{ top: '80px' }}></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
              {steps.map((step, index) => (
                <motion.div 
                  key={step.number}
                  className="relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {/* Card */}
                  <motion.div
                    className={`relative rounded-3xl p-6 h-full ${
                      isDark 
                        ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border border-purple-500/20' 
                        : 'bg-white border border-gray-200 shadow-lg'
                    }`}
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Number Badge - Top Right */}
                    <motion.div 
                      className={`absolute -top-3 -right-3 w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg bg-gradient-to-r ${step.color}`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                    >
                  {step.number}
                    </motion.div>

                    {/* Content */}
                    <div className="pt-4 text-center space-y-3">
                      {/* Icon with Rounded Square Background */}
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl ${step.bgColor} flex items-center justify-center`}>
                        {step.number === 1 && (
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        )}
                        {step.number === 2 && (
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        )}
                        {step.number === 3 && (
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                        )}
                        {step.number === 4 && (
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        )}
                        {step.number === 5 && (
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        )}
                      </div>
                      
                      {/* Title */}
                      <h3 className={`text-xl font-bold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {step.title}
                      </h3>
                      
                      {/* Subtitle */}
                      <p className={`text-sm font-semibold px-3 py-1 rounded-full inline-block ${
                        isDark 
                          ? 'text-purple-300 bg-purple-600/20' 
                          : 'text-purple-700 bg-purple-100'
                      }`}>
                        {step.subtitle}
                      </p>
                      
                      {/* Description */}
                      <p className={`text-sm leading-relaxed ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {step.description}
                      </p>
                </div>
                  </motion.div>

                  {/* Arrow Connector - Desktop Only */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-20 -right-2 text-purple-500 text-3xl z-10">
                      →
              </div>
                  )}
                </motion.div>
            ))}
            </div>
          </div>

          {/* Bottom Message */}
          <motion.div
            className="text-center mt-16 space-y-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className={`text-lg font-medium max-w-3xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              You don't need to be an expert — just curious enough to build something that matters.
            </p>
            
            {/* CTA Button */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
            <Link
              to="/explore"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg text-lg"
              data-testid="explore-active-projects-link"
            >
                Explore Active Projects
                <ArrowRight size={22} />
            </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className={`py-20 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#1a0b2e]' 
          : 'bg-gradient-to-br from-white via-gray-50 to-purple-50'
      }`} data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={`inline-block px-6 py-2 rounded-full text-sm font-semibold ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' 
                : 'bg-purple-100 text-purple-700 border border-purple-300'
            }`}>
              Showcase
            </span>
          </motion.div>

          {/* Title */}
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Featured Projects of the Month
            </h2>
            <p className={`text-lg ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Discover what our builders are creating right now
            </p>
          </motion.div>

       {/* Featured Projects Grid */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
  
          {loading ? (
    <p className="text-center text-gray-500 col-span-full">Loading...</p>
  ) : featuredProjects.length === 0 ? (
    <p className="text-center text-gray-500 col-span-full">No featured projects found.</p>
  ) : (
    featuredProjects.map((project, index) => (
      <motion.div
        key={project.id}
        className={`relative rounded-3xl overflow-hidden flex flex-col ${
          isDark 
            ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border border-purple-500/20' 
            : 'bg-white border border-gray-200 shadow-lg'
        }`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        whileHover={{ y: -8, scale: 1.02 }}
      >

        {/* Status Badge */}
        <div
          className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(project.status)} flex items-center gap-1 z-10`}
        >
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
          {project.status}
        </div>

        {/* Card Content */}
        <div className="p-6 flex flex-col flex-grow">

          {/* Icon */}
          <div
            className={`w-16 h-16 rounded-2xl mb-4 flex items-center justify-center ${
              isDark ? 'bg-purple-600/30' : 'bg-purple-100'
            }`}
          >
            <svg
              className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>

          {/* Project Name */}
          <h3
            className={`text-xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            {project.name}
          </h3>

          {/* Domain */}
          <span
            className={`inline-flex px-3 py-1 rounded-full text-xs font-medium mb-3 w-fit ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300' 
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {project.category}
          </span>

          {/* Tagline */}
          <p
            className={`text-sm mb-6 flex-grow ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {project.tagline}
          </p>

          {/* View Button */}
          <Link to={`/project/${project.id}`} className="mt-auto">
            <motion.button
              className="w-full px-4 py-2 rounded-full text-sm font-semibold transition bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Project →
            </motion.button>
          </Link>
            </div>
      </motion.div>
    ))
  )}

</div>


          {/* View All Projects Link */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Link
              to="/explore"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition ${
                isDark 
                  ? 'text-purple-300 hover:text-purple-200 border border-purple-500/30 hover:bg-purple-600/20' 
                  : 'text-purple-700 hover:text-purple-800 border border-purple-300 hover:bg-purple-50'
              }`}
            >
              View All Projects
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Community Stats Section */}
      <section className={`py-20 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#1a0b2e] via-[#0f0520] to-[#1a0b2e]' 
          : 'bg-gradient-to-br from-purple-50 via-white to-pink-50'
      }`} data-testid="community-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={`inline-block px-6 py-2 rounded-full text-sm font-semibold ${
              isDark 
                ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' 
                : 'bg-purple-100 text-purple-700 border border-purple-300'
            }`}>
              Community Impact
            </span>
          </motion.div>

          {/* Heading */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className={`text-4xl sm:text-5xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Growing Together, Building Better
            </h2>
            <p className={`text-lg ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Join thousands of builders making real-world impact
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Stat 1: Community Members */}
            <motion.div 
              data-testid="stat-members"
              className={`relative rounded-3xl p-8 text-center overflow-hidden ${
                isDark 
                  ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 border border-purple-500/20' 
                  : 'bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -8, scale: 1.05 }}
            >
              {/* Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-purple-600/30' : 'bg-purple-200'
              }`}>
                <svg className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              
              {/* Number */}
              <motion.div 
                className={`text-5xl font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                50,000<span className="text-purple-400">+</span>
              </motion.div>
              
              {/* Label */}
              <div className={`text-sm font-semibold ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Community Members
              </div>
            </motion.div>

            {/* Stat 2: Projects Initiated */}
            <motion.div 
              data-testid="stat-projects"
              className={`relative rounded-3xl p-8 text-center overflow-hidden ${
                isDark 
                  ? 'bg-gradient-to-br from-blue-900/40 to-indigo-900/40 border border-blue-500/20' 
                  : 'bg-gradient-to-br from-blue-100 to-indigo-100 border border-blue-200'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -8, scale: 1.05 }}
            >
              {/* Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-blue-600/30' : 'bg-blue-200'
              }`}>
                <svg className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              
              {/* Number */}
              <motion.div 
                className={`text-5xl font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                120<span className="text-blue-400">+</span>
              </motion.div>
              
              {/* Label */}
              <div className={`text-sm font-semibold ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Projects Initiated
              </div>
            </motion.div>

            {/* Stat 3: Domains Explored */}
            <motion.div 
              data-testid="stat-domains"
              className={`relative rounded-3xl p-8 text-center overflow-hidden ${
                isDark 
                  ? 'bg-gradient-to-br from-green-900/40 to-teal-900/40 border border-green-500/20' 
                  : 'bg-gradient-to-br from-green-100 to-teal-100 border border-green-200'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -8, scale: 1.05 }}
            >
              {/* Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-green-600/30' : 'bg-green-200'
              }`}>
                <svg className={`w-8 h-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              
              {/* Number */}
              <motion.div 
                className={`text-5xl font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                8<span className="text-green-400">+</span>
              </motion.div>
              
              {/* Label */}
              <div className={`text-sm font-semibold ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Domains Explored
            </div>
            </motion.div>

            {/* Stat 4: Featured Builders */}
            <motion.div 
              data-testid="stat-builders"
              className={`relative rounded-3xl p-8 text-center overflow-hidden ${
                isDark 
                  ? 'bg-gradient-to-br from-pink-900/40 to-rose-900/40 border border-pink-500/20' 
                  : 'bg-gradient-to-br from-pink-100 to-rose-100 border border-pink-200'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -8, scale: 1.05 }}
            >
              {/* Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                isDark ? 'bg-pink-600/30' : 'bg-pink-200'
              }`}>
                <svg className={`w-8 h-8 ${isDark ? 'text-pink-400' : 'text-pink-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
            </div>
              
              {/* Number */}
              <motion.div 
                className={`text-5xl font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                30<span className="text-pink-400">+</span>
              </motion.div>
              
              {/* Label */}
              <div className={`text-sm font-semibold ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Featured Builders
            </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <p className={`text-lg font-medium mb-6 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Be part of something bigger. Build with us.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/community"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg"
              >
                Join Our Community
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className={`relative py-20 overflow-hidden transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-[#1a0b2e] via-purple-900 to-pink-900' 
          : 'bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600'
      }`} data-testid="cta-section">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-pink-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Heading */}
            <motion.h2 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Ready to build something real?
            </motion.h2>

            {/* Subtext */}
            <motion.p 
              className="text-lg sm:text-xl lg:text-2xl mb-10 text-white/90 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Join the next Real World Product Challenge today. Transform your ideas into working products, connect with passionate builders, and make an impact that matters.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {/* Primary CTA: Join Now */}
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
          <Link
            to="/community"
                  className="inline-flex items-center gap-2 bg-white text-purple-700 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition shadow-2xl hover:shadow-white/50 border-4 border-white/20"
            data-testid="cta-join-community"
          >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Join Now
                </Link>
              </motion.div>

              {/* Secondary CTA: Submit an Idea */}
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/submit-idea"
                  className="inline-flex items-center gap-2 bg-transparent border-4 border-white text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-white/10 transition shadow-lg backdrop-blur-sm"
                  data-testid="cta-submit-idea"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Submit an Idea
          </Link>
              </motion.div>
            </motion.div>

            {/* Stats/Social Proof */}
            <motion.div
              className="mt-12 pt-8 border-t border-white/20"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <p className="text-white/80 text-sm sm:text-base mb-4">
                Join 50,000+ builders already making an impact
              </p>
              <div className="flex justify-center items-center gap-8 flex-wrap">
                <div className="flex items-center gap-2 text-white/90">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">Free to Join</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">No Experience Required</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">Start Building Today</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Gradient Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400"></div>
      </section>
    </div>
  );
};

export default HomePage;
